import clsx from 'clsx'
import {useEffect} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {SettingsMenu} from '../../../../app/modules/settings'
import {BranchesMenu, useOrganizationBranches} from '../../../../app/modules/organizations'
import {KTSVG} from '../../../helpers'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import {useAsideProvider} from '../../../providers/AsideProvider'
import {useLayout} from '../../core'
import {AsideMenuLayout} from '../aside'
import {isEnterprise, useAuth} from '../../../../app/modules/auth'
import {DrawerComponent} from '../../../assets/ts/components'
import {LAPTOP_BREAKPOINT, XL_BREAKPOINT, ROUTES} from '../../../constants'
import UseRoutesPermission from '../../../hooks/UseRoutesPermission'

const AsideLayout = () => {
  const {classes} = useLayout()
  const {width, isMobileTablet, isWindows} = useWindowDimensions()
  const {activeFooter, setActiveFooter} = useAsideProvider()
  const {currentUser} = useAuth()
  const {pathname} = useLocation()
  const {routes, firstPage} = UseRoutesPermission()
  const {menuData, setMenuDataFromAside} = useOrganizationBranches()
  const logoTo =
    currentUser && routes.ORDERS_VIEW.hasPermission
      ? ROUTES.ORDERS.BASE
      : firstPage?.route ?? ROUTES.SUPPORT.BASE
  const isSettingsPage =
    pathname === ROUTES.SETTINGS.BASE || pathname.startsWith(`${ROUTES.SETTINGS.BASE}/`)
  const isOrganizationsPage =
    pathname === ROUTES.ORGANIZATIONS.BASE || pathname.startsWith(`${ROUTES.ORGANIZATIONS.BASE}/`)
  const shouldShowSettingsMenu = isSettingsPage && width >= LAPTOP_BREAKPOINT
  const shouldShowBranchesMenu =
    isOrganizationsPage && width >= XL_BREAKPOINT && currentUser && isEnterprise(currentUser)
  const shouldShowCustomMenu = shouldShowSettingsMenu || shouldShowBranchesMenu
  const shouldHideAsideOnDesktop = !shouldShowCustomMenu && width >= LAPTOP_BREAKPOINT
  const isDrawerAsideViewport = width < 778
  const drawerActivate = shouldShowBranchesMenu ? 'false' : '{default: true, xxl: false}'

  useEffect(() => {
    const drawer = DrawerComponent.getInstance('gori_aside')
    drawer?.update()
  }, [shouldHideAsideOnDesktop, isDrawerAsideViewport, shouldShowBranchesMenu, width])

  useEffect(() => {
    if (shouldShowBranchesMenu) {
      return
    }

    DrawerComponent.getInstance('gori_aside')?.hide()
  }, [pathname, shouldShowBranchesMenu])

  const handleResetMenu = () => {
    const nodeList = document.querySelectorAll(
      '#kt_accordion_menu_main_aside--main .aside--main__center__item'
    )
    const filteredList: any = []
    nodeList.forEach((item) => {
      if (!item?.classList?.contains('child') && !item?.classList?.contains('active')) {
        filteredList.push(item)
      } else if (item?.classList?.contains('active')) {
        const itemChild: any = item?.childNodes?.[0]?.childNodes?.[0]
        if (itemChild && itemChild?.classList?.contains('collapsed')) {
          itemChild.click()
        }
      }
    })
    filteredList.forEach((item) => {
      const element = item?.childNodes?.[0]?.childNodes?.[0]
      if (!element?.classList?.contains('collapsed')) {
        element.click()
      }
    })
  }

  // reset on mobile
  useEffect(() => {
    const handleClassChange = () => {
      const element = document.getElementById('gori_aside')
      if (!element?.classList?.contains('drawer-on')) {
        handleResetMenu()
      }
    }

    const observer = new MutationObserver(handleClassChange)
    const config = {attributes: true, attributeFilter: ['class']}
    const element = document.getElementById('gori_aside')
    if (element && isMobileTablet) {
      observer.observe(element, config)
    }

    return () => {
      if (element) {
        observer.disconnect()
      }
    }
  }, [isMobileTablet])
  // BEGIN: Event footer aside
  useEffect(() => {
    const handleClassChange = () => {
      if (activeFooter) {
        const element: any = document.getElementById(
          `aside-footer-${activeFooter === '/settings' ? 'settings' : 'accounts'}`
        )
        if (!element?.classList?.contains('show')) {
          setActiveFooter((prev) => {
            return activeFooter !== prev ? prev : null
          })
        }
      }
    }

    const observer = new MutationObserver(handleClassChange)
    const config = {attributes: true, attributeFilter: ['class']}
    const element = document.getElementById(
      `aside-footer-${activeFooter === '/settings' ? 'settings' : 'accounts'}`
    )
    if (element) {
      observer.observe(element, config)
    }

    return () => {
      if (element) {
        observer.disconnect()
      }
    }
  }, [activeFooter, isWindows, setActiveFooter])

  const handleToggleClick = () => {
    setActiveFooter(null)
    handleResetMenu()
  }

  useEffect(() => {
    const toggleElement = document.getElementById('gori_aside_toggle')
    if (toggleElement) {
      toggleElement.addEventListener('click', handleToggleClick)
    }

    return () => {
      if (toggleElement) {
        toggleElement.removeEventListener('click', handleToggleClick)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // END: Event footer aside

  return (
    <div className='aside'>
      <div
        id='gori_aside'
        className={clsx(
          'aside--main flex-column justify-content-between',
          classes.aside.join(' '),
          shouldShowBranchesMenu && 'organizations-aside-pinned',
          shouldShowCustomMenu ? 'd-flex' : shouldHideAsideOnDesktop ? 'd-none' : null
        )}
        data-kt-drawer='true'
        data-kt-drawer-name='aside'
        data-kt-drawer-activate={drawerActivate}
        data-kt-drawer-overlay='true'
        data-kt-drawer-width="{default: '240px'}"
        data-kt-drawer-direction='start'
        data-kt-drawer-toggle='#gori_aside_toggle'
      >
        <div className='mb-6 mb-xxl-20'>
          <div
            className='d-flex justify-content-start mb-6 mb-xxl-9 mt-1'
            id='gori_aside_logo'
            style={{padding: '20px 35px'}}
          >
            <Link to={logoTo}>
              <KTSVG path='/media/logos/shipbae-logo-black.svg' svgClassName='' small={false} />
            </Link>
          </div>
          <div id='gori_aside_menu' className='aside--main__center scroll-y'>
            {shouldShowSettingsMenu ? (
              <div className='settings-menu-content'>
                <SettingsMenu />
              </div>
            ) : shouldShowBranchesMenu ? (
              <BranchesMenu menuData={menuData} onMenuDataChange={setMenuDataFromAside} />
            ) : (
              <AsideMenuLayout className='aside--main' />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export {AsideLayout}
